import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaPlus, FaUser, FaEnvelope, FaTasks, FaClipboardList, FaFileInvoice, FaMoneyCheckAlt, FaTag, FaFolderOpen, FaMoneyBillWave, FaHome, FaUserPlus, FaCog, FaSignOutAlt, FaPaperPlane, FaBell, FaProjectDiagram, FaInbox, FaChartBar } from 'react-icons/fa';
import { Box } from '@mui/material';
import { AuthContext } from './AuthContext';
import { EmailSuggestionsContext } from '../contexts/EmailSuggestionsContext';
import ClientForm from './ClientForm';
import ClientSidebar from '../ClientSidebar';
import ComposeEmailDialog from './email/ComposeEmailDialog';
import CreateRequestDialog from './client-requests/CreateRequestDialog';
import CreateLetterDialog from './letters/CreateLetterDialog';
import './Sidebar.css';

const Sidebar = ({ onOptionSelect }) => {
  const [showSubMenu, setShowSubMenu] = useState(false);
  const [showSettingsPanel, setShowSettingsPanel] = useState(false);
  const [showBillingSubMenu, setShowBillingSubMenu] = useState(false);
  const [showTasksSubMenu, setShowTasksSubMenu] = useState(false);
  const [showWorkflowsSubMenu, setShowWorkflowsSubMenu] = useState(false);
  const [showReportsSubMenu, setShowReportsSubMenu] = useState(false);
  const [showClientForm, setShowClientForm] = useState(false);
  const [composeEmailOpen, setComposeEmailOpen] = useState(false);
  const [createRequestOpen, setCreateRequestOpen] = useState(false);
  const [createLetterOpen, setCreateLetterOpen] = useState(false);

  const [showClientSidebar, setShowClientSidebar] = useState(false);
  const [userInitials, setUserInitials] = useState('');
  const navigate = useNavigate();
  const sidebarRef = useRef(null);
  const { userPermissions, logout } = useContext(AuthContext) || {};
  const { count: inboxSuggestionCount } = useContext(EmailSuggestionsContext) || {};

  useEffect(() => {
    // Use the new multi-tenant authentication system
    const user = localStorage.getItem('user');
    if (user) {
      try {
        const userData = JSON.parse(user);
        const username = userData.username || userData.first_name || '';
        setUserInitials(username.slice(0, 2).toUpperCase());
      } catch (error) {
        console.error('Error parsing user data:', error);
        setUserInitials('');
      }
    }
  }, []);

  const toggleSubMenu = () => {
    setShowSubMenu(prev => !prev);
    setShowSettingsPanel(false);
    setShowBillingSubMenu(false);
    setShowTasksSubMenu(false);
    setShowWorkflowsSubMenu(false);
  };

  const toggleSettingsPanel = () => {
    setShowSettingsPanel(prev => !prev);
    setShowSubMenu(false);
    setShowBillingSubMenu(false);
    setShowTasksSubMenu(false);
    setShowWorkflowsSubMenu(false);
    setShowReportsSubMenu(false);
  };

  const toggleBillingSubMenu = () => {
    setShowBillingSubMenu(prev => !prev);
    setShowSubMenu(false);
    setShowSettingsPanel(false);
    setShowTasksSubMenu(false);
    setShowWorkflowsSubMenu(false);
    setShowReportsSubMenu(false);
  };

  const toggleTasksSubMenu = () => {
    setShowTasksSubMenu(prev => !prev);
    setShowSubMenu(false);
    setShowSettingsPanel(false);
    setShowBillingSubMenu(false);
    setShowWorkflowsSubMenu(false);
  };

  const toggleWorkflowsSubMenu = () => {
    setShowWorkflowsSubMenu(prev => !prev);
    setShowSubMenu(false);
    setShowSettingsPanel(false);
    setShowBillingSubMenu(false);
    setShowTasksSubMenu(false);
    setShowReportsSubMenu(false);
  };

  const toggleReportsSubMenu = () => {
    setShowReportsSubMenu(prev => !prev);
    setShowSubMenu(false);
    setShowSettingsPanel(false);
    setShowBillingSubMenu(false);
    setShowTasksSubMenu(false);
    setShowWorkflowsSubMenu(false);
  };

  const toggleClientSidebar = () => {
    setShowClientSidebar(prev => !prev);
    setShowClientForm(false);
    setShowSubMenu(false);
    setShowSettingsPanel(false);
    setShowBillingSubMenu(false);
    setShowTasksSubMenu(false);
    setShowWorkflowsSubMenu(false);
    setShowReportsSubMenu(false);
  };

  const handleClientFormClose = () => {
    setShowClientForm(false);
  };

  const handleAddClient = (clientData) => {
    setShowClientForm(false);
    // Optionally refresh dashboard or show success message
  };

  const handleNavigation = (option) => {
    setShowSubMenu(false);
    setShowSettingsPanel(false);
    setShowBillingSubMenu(false);
    setShowWorkflowsSubMenu(false);
    if (option === 'clientSidebar') {
      onOptionSelect(prev => prev === 'clientSidebar' ? null : 'clientSidebar');
    } else if (option === 'notifications') {
      navigate('/notifications');
    } else if (option === 'inbox') {
      navigate('/inbox');
    } else if (option === 'settings') {
      navigate('/settings');
    } else if (option === 'activity-log') {
      navigate('/activity-log');
    } else if (option === 'workflows') {
      navigate('/workflows');
    } else if (option === 'workflows-analytics') {
      navigate('/workflows/analytics');
    } else if (option === 'workflows-kanban') {
      navigate('/workflows/kanban');
    } else if (option === 'workflows-templates') {
      navigate('/workflows/templates');
    } else if (option === 'tax-organizer') {
      navigate('/tax-organizers');
    } else if (option === 'suggest-feature') {
      navigate('/suggest-feature');
    } else if (option === 'add-user') {
      navigate('/add-user');
    } else if (option === 'folder') {
      navigate('/files/clients/root');
    } else if (option === 'home') {
      navigate('/dashboard');
    } else if (option === 'add-tag') {
      navigate('/add-tag');
    } else if (option === 'reports-profitability') {
      navigate('/reports/profitability');
    } else if (option === 'reports-forecast') {
      navigate('/reports/forecast');
    } else if (option === 'reports-staff') {
      navigate('/reports/staff');
    } else if (option === 'tasks') {
      navigate('/tasks');
    } else if (option === 'tasks-active') {
      navigate('/tasks?view=active');
    } else if (option === 'tasks-all') {
      navigate('/tasks?view=all');
    } else if (option === 'tasks-my') {
      navigate('/tasks?view=my');
    } else if (option === 'tasks-tax-prep') {
      navigate('/tasks?view=tax_prep');
    } else if (option === 'tasks-create') {
      navigate('/tasks?create=1');
    } else if (option === 'calendar') {
      navigate('/events');
    } else if (option === 'profile') {
      navigate('/profile');
    } else if (option === 'logout') {
      if (logout) {
        logout();
      } else {
        ['access_token', 'refresh_token', 'user', 'userId', 'jwtToken', 'refreshToken'].forEach((k) => localStorage.removeItem(k));
        navigate('/login');
      }
    } else if (option === 'email') {
      setComposeEmailOpen(true);
    } else if (option === 'clientRequest') {
      setCreateRequestOpen(true);
    } else if (option === 'letter') {
      setCreateLetterOpen(true);
    } else if (option === 'addClient') {
      setShowClientSidebar(false);
      setShowClientForm(true);
    } else if (option.startsWith('billing-')) {
      if (userPermissions?.can_access_billing_settings) {
        const route = option.replace('billing-', '');
        navigate(`/billing/${route}`);
      } else {
        alert('No access to billing features.');
      }
    } else {
      onOptionSelect(option);
    }
  };

  useEffect(() => {
    const handleOutsideClick = (e) => {
      if (sidebarRef.current && !sidebarRef.current.contains(e.target)) {
        setShowSubMenu(false);
        setShowSettingsPanel(false);
        setShowBillingSubMenu(false);
        setShowTasksSubMenu(false);
        setShowWorkflowsSubMenu(false);
        setShowClientSidebar(false);
      }

    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  return (
    <>
    <div className="sidebar" ref={sidebarRef} style={{ background: '#0f172a', color: '#fff' }}>
      <div className="sidebar-nav-group">
      <div className="sidebar-button" onClick={() => handleNavigation('home')}>
        <FaHome className="plus-icon" style={{ color: '#fff' }} />
      </div>

      <div className="sidebar-button" onClick={toggleSubMenu}>
        <FaPlus className="plus-icon" style={{ color: '#fff' }} />
      </div>
      {showSubMenu && (
        <div className="create-panel-modern" style={{ color: '#111827' }}>
          <div className="create-panel-header" style={{ color: '#111827' }}>
            <span>Create</span>
            <span className="close-icon" onClick={toggleSubMenu}>&times;</span>
          </div>
          <div className="create-panel-icons">
            <div className="create-panel-icon" onClick={() => handleNavigation('addClient')}>
              <FaUserPlus />
              <div style={{ color: '#111827' }}>Add Client</div>
            </div>
            <div className="create-panel-icon" onClick={() => handleNavigation('clientRequest')}>
              <FaPaperPlane />
              <div style={{ color: '#111827' }}>Client Request</div>
            </div>
            <div className="create-panel-icon" onClick={() => handleNavigation('email')}>
              <FaEnvelope />
              <div style={{ color: '#111827' }}>Email</div>
            </div>
          </div>
          <div className="create-panel-section" onClick={() => handleNavigation('letter')}>
            <span className="create-panel-link">Letter <span className="arrow">&#8250;</span></span>
            <div className="create-panel-desc">Create a new or templated letter to send to your clients.</div>
          </div>
          <div className="create-panel-section" onClick={() => handleNavigation('calendar')}>
            <span className="create-panel-link">Calendar Event <span className="arrow">&#8250;</span></span>
            <div className="create-panel-desc">Create an event for your clients, team members or yourself.</div>
          </div>
          <div className="create-panel-section" onClick={() => handleNavigation('tax-organizer')}>
            <span className="create-panel-link">Tax Organizer <span className="arrow">&#8250;</span></span>
            <div className="create-panel-desc">Gather all the necessary files and information to prepare returns.</div>
          </div>
        </div>
      )}

      <div className="sidebar-button" onClick={toggleClientSidebar}>
        <FaUser className="plus-icon" style={{ color: '#fff' }} />
      </div>

      <div className="sidebar-button" onClick={() => handleNavigation('folder')}>
        <FaFolderOpen className="plus-icon" style={{ color: '#fff' }} />
      </div>
      <div className="sidebar-button" onClick={() => handleNavigation('inbox')} title="Inbox" style={{ position: 'relative' }}>
        <FaInbox className="plus-icon" style={{ color: '#fff' }} />
        {(inboxSuggestionCount ?? 0) > 0 && (
          <Box
            component="span"
            sx={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 8,
              height: 8,
              borderRadius: '50%',
              bgcolor: 'error.main',
            }}
            aria-label={`${inboxSuggestionCount} suggestion(s)`}
          />
        )}
      </div>
      <div className="sidebar-button" onClick={toggleTasksSubMenu} title="Tasks">
        <FaTasks className="plus-icon" style={{ color: '#fff' }} />
      </div>
      {showTasksSubMenu && (
        <div className="billing-menu-modern" style={{ top: 200 }}>
          <div className="billing-menu-item" onClick={() => handleNavigation('tasks-active')}>
            Active work <span className="billing-menu-arrow">&#8250;</span>
          </div>
          <div className="billing-menu-item" onClick={() => handleNavigation('tasks-all')}>
            All work <span className="billing-menu-arrow">&#8250;</span>
          </div>
          <div className="billing-menu-item" onClick={() => handleNavigation('tasks-my')}>
            My work <span className="billing-menu-arrow">&#8250;</span>
          </div>
          <div className="billing-menu-item" onClick={() => handleNavigation('tasks-tax-prep')}>
            Tax prep Queue <span className="billing-menu-arrow">&#8250;</span>
          </div>
          <div className="billing-menu-item billing-menu-item-divider" onClick={() => handleNavigation('tasks-create')}>
            Create task <span className="billing-menu-arrow">&#8250;</span>
          </div>
        </div>
      )}
      <div className="sidebar-button" onClick={() => handleNavigation('add-user')}>
        <FaUserPlus className="plus-icon" style={{ color: '#fff' }} />
      </div>
      <div className="sidebar-button" onClick={toggleBillingSubMenu}>
        <FaMoneyBillWave className="plus-icon" style={{ color: '#fff' }} />
      </div>
      {showBillingSubMenu && (
        <div className="billing-menu-modern" style={{ top: 140 }}>
          {userPermissions?.can_view_invoices || userPermissions?.can_access_credits || userPermissions?.can_access_payments ? (
            <>
              <div className="billing-menu-item" onClick={() => handleNavigation('billing-dashboard')}>
                Dashboard <span className="billing-menu-arrow">&#8250;</span>
              </div>
              <div className="billing-menu-item" onClick={() => handleNavigation('billing-invoices')}>
                Invoices <span className="billing-menu-arrow">&#8250;</span>
              </div>
              <div className="billing-menu-item" onClick={() => handleNavigation('billing-payments')}>
                Payments <span className="billing-menu-arrow">&#8250;</span>
              </div>
              <div className="billing-menu-item" onClick={() => handleNavigation('billing-credits')}>
                Credits <span className="billing-menu-arrow">&#8250;</span>
              </div>
              <div className="billing-menu-item" onClick={() => handleNavigation('billing-expenses')}>
                Expenses <span className="billing-menu-arrow">&#8250;</span>
              </div>
              <div className="billing-menu-item" onClick={() => handleNavigation('billing-statements')}>
                Statements <span className="billing-menu-arrow">&#8250;</span>
              </div>
              <div className="billing-menu-item" onClick={() => handleNavigation('billing-reports')}>
                Reports <span className="billing-menu-arrow">&#8250;</span>
              </div>
            </>
          ) : (
            <div className="billing-menu-item no-access">
              <span>No access to billing features.</span>
            </div>
          )}
        </div>
      )}
      <div className="sidebar-button" onClick={toggleWorkflowsSubMenu} title="Workflows">
        <FaProjectDiagram className="plus-icon" style={{ color: '#fff' }} />
      </div>
      <div className="sidebar-button" onClick={toggleReportsSubMenu} title="Reports">
        <FaChartBar className="plus-icon" style={{ color: '#fff' }} />
      </div>
      {showReportsSubMenu && (
        <div className="billing-menu-modern" style={{ top: 340 }}>
          <div className="billing-menu-item" onClick={() => handleNavigation('reports-profitability')}>
            Profitability Report <span className="billing-menu-arrow">&#8250;</span>
          </div>
          <div className="billing-menu-item" onClick={() => handleNavigation('reports-forecast')}>
            Forecast <span className="billing-menu-arrow">&#8250;</span>
          </div>
          <div className="billing-menu-item" onClick={() => handleNavigation('reports-staff')}>
            Staff Utilization <span className="billing-menu-arrow">&#8250;</span>
          </div>
        </div>
      )}
      {showWorkflowsSubMenu && (
        <div className="billing-menu-modern" style={{ top: 280 }}>
          <div className="billing-menu-item" onClick={() => handleNavigation('workflows')}>
            Workflows <span className="billing-menu-arrow">&#8250;</span>
          </div>
          <div className="billing-menu-item" onClick={() => handleNavigation('workflows-kanban')}>
            Kanban Board <span className="billing-menu-arrow">&#8250;</span>
          </div>
          <div className="billing-menu-item" onClick={() => handleNavigation('workflows-analytics')}>
            <FaChartBar style={{ marginRight: 6 }} />Analytics <span className="billing-menu-arrow">&#8250;</span>
          </div>
          <div className="billing-menu-item" onClick={() => handleNavigation('workflows-templates')}>
            Templates <span className="billing-menu-arrow">&#8250;</span>
          </div>
        </div>
      )}
      <div className="sidebar-button" onClick={() => handleNavigation('add-tag')}>
        <FaTag className="plus-icon" style={{ color: '#fff' }} />
      </div>
      </div>

      <div className="sidebar-footer">
      <div className="sidebar-button profile-button" onClick={toggleSettingsPanel}>
        <div className="profile-icon">{userInitials || 'NA'}</div>
      </div>
      </div>
      {showSettingsPanel && (
        <div className="profile-menu-modern">
          <div className="profile-menu-item" onClick={() => handleNavigation('profile')}>
            Profile <span className="profile-menu-arrow">&#8250;</span>
          </div>
          <div className="profile-menu-item" onClick={() => handleNavigation('activity-log')}>
            Activity log <span className="profile-menu-arrow">&#8250;</span>
          </div>
          <div className="profile-menu-item" onClick={() => handleNavigation('suggest-feature')}>
            Suggest a feature <span className="profile-menu-arrow">&#8250;</span>
          </div>
          <div className="profile-menu-item" onClick={() => handleNavigation('settings')}>
            Settings <span className="profile-menu-arrow">&#8250;</span>
          </div>
          {(() => {
            try {
              const userData = JSON.parse(localStorage.getItem('user') || '{}');
              if (userData.is_superuser) {
                return (
                  <div className="profile-menu-item" onClick={() => { navigate('/platform-admin'); setShowSettingsPanel(false); }}>
                    Platform Admin <span className="profile-menu-arrow">&#8250;</span>
                  </div>
                );
              }
            } catch { /* ignore */ }
            return null;
          })()}
          <div className="profile-menu-item" onClick={() => handleNavigation('logout')}>
            Sign out
          </div>
        </div>
      )}
    </div>

      {/* ClientSidebar Overlay — outside flex sidebar so portals don’t affect rail layout */}
      {showClientSidebar && (
        <ClientSidebar onClose={() => setShowClientSidebar(false)} />
      )}

      {showClientForm && (
        <ClientForm
          open={showClientForm}
          onClose={handleClientFormClose}
          onAddClient={handleAddClient}
          useDrawer={false}
        />
      )}

      <ComposeEmailDialog
        open={composeEmailOpen}
        onClose={() => setComposeEmailOpen(false)}
        onSent={() => setComposeEmailOpen(false)}
      />

      <CreateRequestDialog
        open={createRequestOpen}
        onClose={() => setCreateRequestOpen(false)}
        onCreated={() => setCreateRequestOpen(false)}
      />

      <CreateLetterDialog
        open={createLetterOpen}
        onClose={() => setCreateLetterOpen(false)}
        onCreated={() => setCreateLetterOpen(false)}
      />
    </>
  );
};

export default Sidebar;


